import { useQuery } from "@tanstack/react-query";
import { ArrowUpDown, Search, X } from "lucide-react";
import { format } from "date-fns";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useUrlState } from "@/hooks/use-url-state";
import { apiRequest } from "@/lib/queryClient";
import type { CommissionTransaction } from "@shared/schema";
import { Link } from "wouter";

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

type SortField = "date" | "lender" | "type" | "amount" | "total" | "status" | "partner";
type SortDirection = "asc" | "desc";

const PROCESSING_STATUS_CONFIG: Record<
  string,
  { label: string; className: string }
> = {
  pending: { label: "Pending", className: "bg-slate-100 text-slate-700 border-slate-300" },
  imported: { label: "Imported", className: "bg-blue-100 text-blue-700 border-blue-300" },
  matched: { label: "Matched", className: "bg-green-100 text-green-700 border-green-300" },
  calculated: { label: "Calculated", className: "bg-yellow-100 text-yellow-700 border-yellow-300" },
  paid: { label: "Paid", className: "bg-emerald-100 text-emerald-700 border-emerald-300" },
  disputed: { label: "Disputed", className: "bg-red-100 text-red-700 border-red-300" },
  void: { label: "Void", className: "bg-slate-100 text-slate-500 border-slate-200" },
};

const COMMISSION_TYPE_OPTIONS = [
  { value: "none", label: "All Types" },
  { value: "upfront", label: "Upfront" },
  { value: "trail", label: "Trail" },
];

const STATUS_OPTIONS = [
  { value: "none", label: "All Statuses" },
  { value: "pending", label: "Pending" },
  { value: "imported", label: "Imported" },
  { value: "matched", label: "Matched" },
  { value: "calculated", label: "Calculated" },
  { value: "paid", label: "Paid" },
  { value: "disputed", label: "Disputed" },
  { value: "void", label: "Void" },
];

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const formatCurrency = (value: string | null | undefined): string => {
  if (!value) return "-";
  const num = parseFloat(value);
  if (isNaN(num)) return "-";
  return new Intl.NumberFormat("en-AU", {
    style: "currency",
    currency: "AUD",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(num);
};

const formatDate = (value: string | null | undefined): string => {
  if (!value) return "-";
  try {
    return format(new Date(value), "dd/MM/yyyy");
  } catch {
    return value;
  }
};

// ---------------------------------------------------------------------------
// Sortable column header
// ---------------------------------------------------------------------------

interface SortHeaderProps {
  label: string;
  field: SortField;
  currentSort: SortField;
  currentDirection: SortDirection;
  onSort: (field: SortField) => void;
}

function SortHeader({
  label,
  field,
  currentSort,
  currentDirection: _currentDirection,
  onSort,
}: SortHeaderProps) {
  const isActive = currentSort === field;
  return (
    <button
      type="button"
      onClick={() => onSort(field)}
      className="flex items-center gap-1 font-semibold hover:text-foreground transition-colors"
      data-testid={`sort-header-${field}`}
    >
      {label}
      <span data-testid={isActive ? `sort-indicator-${field}` : undefined}>
        <ArrowUpDown
          className={`h-3 w-3 ${isActive ? "text-primary" : "text-muted-foreground"}`}
        />
      </span>
    </button>
  );
}

// ---------------------------------------------------------------------------
// Status badge
// ---------------------------------------------------------------------------

interface StatusBadgeProps {
  status: string;
}

function StatusBadge({ status }: StatusBadgeProps) {
  const config = PROCESSING_STATUS_CONFIG[status] ?? {
    label: status,
    className: "bg-gray-100 text-gray-700 border-gray-300",
  };
  return (
    <Badge
      variant="outline"
      className={config.className}
      data-testid={`status-badge-${status}`}
    >
      {config.label}
    </Badge>
  );
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

export default function CommissionTransactionsPage() {
  // Filters — URL persisted
  const [search, setSearch] = useUrlState("search", "", { debounceMs: 300 });
  const [commissionType, setCommissionType] = useUrlState("commissionType", "none");
  const [status, setStatus] = useUrlState("status", "none");
  const [requiresReview, setRequiresReview] = useUrlState<boolean>(
    "requiresReview",
    false
  );

  // Sorting — URL persisted
  const [sortField, setSortField] = useUrlState<SortField>("sort", "date");
  const [sortDirection, setSortDirection] = useUrlState<SortDirection>(
    "dir",
    "desc"
  );

  // Build query params
  const queryParams = new URLSearchParams();
  if (search) queryParams.set("search", search);
  if (commissionType !== "none") queryParams.set("commissionType", commissionType);
  if (status !== "none") queryParams.set("processingStatus", status);
  if (requiresReview) queryParams.set("requiresReview", "true");
  queryParams.set("limit", "200");

  const { data, isLoading } = useQuery<CommissionTransaction[]>({
    queryKey: [
      "/api/commissions/transactions",
      search,
      commissionType,
      status,
      requiresReview,
    ],
    queryFn: async () => {
      const url = `/api/commissions/transactions?${queryParams.toString()}`;
      const response = await apiRequest("GET", url);
      return response.json();
    },
  });

  const transactions = Array.isArray(data) ? data : [];

  // Client-side sort
  const sorted = [...transactions].sort((a, b) => {
    let aVal: string | number = 0;
    let bVal: string | number = 0;

    switch (sortField) {
      case "date":
        aVal = a.transactionDate ?? "";
        bVal = b.transactionDate ?? "";
        break;
      case "lender":
        aVal = (a.lenderName ?? "").toLowerCase();
        bVal = (b.lenderName ?? "").toLowerCase();
        break;
      case "type":
        aVal = (a.commissionType ?? "").toLowerCase();
        bVal = (b.commissionType ?? "").toLowerCase();
        break;
      case "amount":
        aVal = parseFloat(a.commissionAmount ?? "0");
        bVal = parseFloat(b.commissionAmount ?? "0");
        break;
      case "total":
        aVal = parseFloat(a.totalAmount ?? "0");
        bVal = parseFloat(b.totalAmount ?? "0");
        break;
      case "status":
        aVal = (a.processingStatus ?? "").toLowerCase();
        bVal = (b.processingStatus ?? "").toLowerCase();
        break;
      case "partner":
        aVal = (a.matchedPartnerId ?? "").toLowerCase();
        bVal = (b.matchedPartnerId ?? "").toLowerCase();
        break;
    }

    if (aVal < bVal) return sortDirection === "asc" ? -1 : 1;
    if (aVal > bVal) return sortDirection === "asc" ? 1 : -1;
    return 0;
  });

  const toggleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortDirection("asc");
    }
  };

  const hasActiveFilters =
    search !== "" ||
    commissionType !== "none" ||
    status !== "none" ||
    requiresReview;

  const clearFilters = () => {
    setSearch("");
    setCommissionType("none");
    setStatus("none");
    setRequiresReview(false);
  };

  return (
    <div className="mx-auto max-w-7xl space-y-4">
      {/* Filters row */}
      <Card className="p-3">
        <div className="flex flex-col gap-3">
          <div className="flex flex-col gap-3 md:flex-row md:items-center">
            {/* Search */}
            <div className="relative flex-1 max-w-sm">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                placeholder="Search account # or client name..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-9 pr-9"
                data-testid="input-search-transactions"
              />
              {search && (
                <button
                  type="button"
                  onClick={() => setSearch("")}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                  aria-label="Clear search"
                >
                  <X className="h-4 w-4" />
                </button>
              )}
            </div>

            {/* Commission type filter */}
            <Select value={commissionType} onValueChange={setCommissionType}>
              <SelectTrigger
                className="w-[150px]"
                data-testid="select-filter-commission-type"
              >
                <SelectValue placeholder="Type" />
              </SelectTrigger>
              <SelectContent>
                {COMMISSION_TYPE_OPTIONS.map((opt) => (
                  <SelectItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            {/* Status filter */}
            <Select value={status} onValueChange={setStatus}>
              <SelectTrigger
                className="w-[160px]"
                data-testid="select-filter-status"
              >
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                {STATUS_OPTIONS.map((opt) => (
                  <SelectItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            {/* Requires Review checkbox */}
            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <Checkbox
                checked={requiresReview}
                onCheckedChange={(checked) =>
                  setRequiresReview(checked === true)
                }
                data-testid="checkbox-requires-review"
              />
              Requires Review
            </label>

            {/* Clear filters */}
            {hasActiveFilters && (
              <Button
                variant="ghost"
                size="sm"
                onClick={clearFilters}
                className="text-muted-foreground"
              >
                <X className="mr-1 h-3 w-3" />
                Clear filters
              </Button>
            )}
          </div>

          {/* Active filter chips */}
          {hasActiveFilters && (
            <div className="text-xs text-muted-foreground">
              Showing {sorted.length} transaction
              {sorted.length !== 1 ? "s" : ""}
            </div>
          )}
        </div>
      </Card>

      {/* Transactions table */}
      <Card>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>
                <SortHeader
                  label="Date"
                  field="date"
                  currentSort={sortField}
                  currentDirection={sortDirection}
                  onSort={toggleSort}
                />
              </TableHead>
              <TableHead>
                <SortHeader
                  label="Lender"
                  field="lender"
                  currentSort={sortField}
                  currentDirection={sortDirection}
                  onSort={toggleSort}
                />
              </TableHead>
              <TableHead>Account #</TableHead>
              <TableHead>Client Name</TableHead>
              <TableHead>
                <SortHeader
                  label="Type"
                  field="type"
                  currentSort={sortField}
                  currentDirection={sortDirection}
                  onSort={toggleSort}
                />
              </TableHead>
              <TableHead className="text-right">
                <SortHeader
                  label="Amount"
                  field="amount"
                  currentSort={sortField}
                  currentDirection={sortDirection}
                  onSort={toggleSort}
                />
              </TableHead>
              <TableHead className="text-right">GST</TableHead>
              <TableHead className="text-right">
                <SortHeader
                  label="Total"
                  field="total"
                  currentSort={sortField}
                  currentDirection={sortDirection}
                  onSort={toggleSort}
                />
              </TableHead>
              <TableHead>
                <SortHeader
                  label="Status"
                  field="status"
                  currentSort={sortField}
                  currentDirection={sortDirection}
                  onSort={toggleSort}
                />
              </TableHead>
              <TableHead>
                <SortHeader
                  label="Partner"
                  field="partner"
                  currentSort={sortField}
                  currentDirection={sortDirection}
                  onSort={toggleSort}
                />
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              // Loading skeleton — 10 placeholder rows
              Array.from({ length: 10 }).map((_, i) => (
                <TableRow key={i}>
                  {Array.from({ length: 10 }).map((__, j) => (
                    <TableCell key={j}>
                      <div className="h-4 w-full animate-pulse rounded bg-muted" />
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : sorted.length === 0 ? (
              <TableRow>
                <TableCell
                  colSpan={10}
                  className="py-12 text-center text-muted-foreground"
                >
                  <div className="space-y-2">
                    <p>No commission transactions found</p>
                    <Link href="/commission/import">
                      <Button variant="outline" size="sm">
                        Import your first commission statement
                      </Button>
                    </Link>
                  </div>
                </TableCell>
              </TableRow>
            ) : (
              sorted.map((tx) => (
                <TableRow
                  key={tx.id}
                  data-testid={`row-transaction-${tx.id}`}
                  className="cursor-default"
                >
                  <TableCell className="whitespace-nowrap">
                    {formatDate(tx.transactionDate)}
                  </TableCell>
                  <TableCell>{tx.lenderName ?? "-"}</TableCell>
                  <TableCell className="font-mono text-sm">
                    {tx.lenderAccountNumber ?? "-"}
                  </TableCell>
                  <TableCell>{tx.clientNameFromFile ?? "-"}</TableCell>
                  <TableCell className="capitalize">
                    {tx.commissionType ?? "-"}
                  </TableCell>
                  <TableCell className="text-right font-medium">
                    {formatCurrency(tx.commissionAmount)}
                  </TableCell>
                  <TableCell className="text-right text-muted-foreground">
                    {formatCurrency(tx.gstAmount ?? null)}
                  </TableCell>
                  <TableCell className="text-right font-medium">
                    {formatCurrency(tx.totalAmount)}
                  </TableCell>
                  <TableCell>
                    <StatusBadge status={tx.processingStatus ?? "pending"} />
                  </TableCell>
                  <TableCell className="text-muted-foreground text-sm">
                    {tx.matchedPartnerId ? (
                      <span title={tx.matchedPartnerId}>
                        {tx.matchedPartnerId.slice(0, 8)}…
                      </span>
                    ) : (
                      "-"
                    )}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </Card>
    </div>
  );
}
